import { useEffect, useMemo, useState } from 'react'

import ChevronLeft from '@mui/icons-material/ChevronLeft'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RedoIcon from '@mui/icons-material/Sync'
import { usePermissions } from 'commons/customHooks/useAuth'
import useDebounce from 'commons/customHooks/useDebounce'
import { sumArray } from 'commons/utils/array'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import {
  Checkbox,
  DSInputType,
  type HeadItemType,
  Icon,
  Input,
  LoadingHavaianas,
  Modal,
  NotFound,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { availableCentersFactories } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import { useNavigate } from 'react-router-dom'
import {
  getRejectedItems,
  replicateRecords,
  setRejectedRecords,
} from 'store/SalesOrderContext/salesOrderSlice'
import type { MaterialsListInterface } from 'store/SalesOrderContext/types/MaterialItemData'
import { setSalesOrderCurrentTab } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

const RejectedItemsTab = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { rejectedRecords, loadingMaterials: loading } = useSelector((state) => state.salesOrder)
  const [searchValue, setSearchValue] = useState('')
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const pageSize = 20
  const getTotalPages = () => Math.ceil(getTotalItems() / pageSize)
  const getTotalItems = () => rejectedRecords.length
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [confirmReplicateModal, setConfirmReplicateModal] = useState(false)

  const factoryFilterOptions = availableCentersFactories
  const [selectedFactoryFilters, setSelectedFactoryFilters] = useState<string[]>([])
  const [appliedFactoryFilters, setAppliedFactoryFilters] = useState<string[]>([])

  interface MaterialStatusOptionsType {
    [key: string]: { label: string; icon: React.ReactElement }
  }
  const materialStatusOptions: MaterialStatusOptionsType = {
    'SEM COTA': {
      icon: <Icon color={theme.colors.alert.warning.dark} icon="report" size="15" />,
      label: 'Indisponível por cota',
    },
    'SEM ESTOQUE': {
      icon: <Icon color={theme.colors.alert.warning.dark} icon="box" size="15" />,
      label: 'Indisponível por estoque',
    },
  }
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([])
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([])

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action)
  }

  useEffect(() => {
    dispatch(getRejectedItems())
  }, [])

  const dispatchSearch = (value: string) => {
    dispatch(getRejectedItems(value, appliedFactoryFilters, appliedStatusFilters))
  }
  const debounceSearch = useDebounce(dispatchSearch, 1000)
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceSearch(value)
  }

  const handleLink = (order: string) => {
    dispatch(setSalesOrderCurrentTab(1))
    navigate(`${URL_CONST.MANAGE_ORDER}/${order}`)
  }
  const clearFilters = () => {
    setSelectedFactoryFilters([])
    setAppliedFactoryFilters([])
    setSelectedStatusFilters([])
    setAppliedStatusFilters([])
    setSearchValue('')
    dispatch(getRejectedItems())
    setOpenFilterModal(false)
  }

  const sizesSet = useMemo(() => {
    const memoSet = new Set<string>()
    rejectedRecords.forEach(({ tamanhos }) => {
      tamanhos.forEach(({ cd_tamh }) => {
        const t = { size: cd_tamh }
        memoSet.add(JSON.stringify(t))
      })
    })
    return memoSet
  }, [rejectedRecords])

  const tooltipLabel = (record: MaterialsListInterface) => {
    if (record.nr_pedi_rasc_dupl) return 'Reutilizado em novo pedido. Clique para acessar'
    if (record.ds_moti_crit === 'SEM COTA') return 'Indisponível por cota'
    if (record.ds_moti_crit === 'SEM ESTOQUE') return 'Indisponível por estoque'
    return ''
  }
  const tooltipIcon = (record: MaterialsListInterface) => {
    if (record.nr_pedi_rasc_dupl)
      return <Icon color={theme.colors.neutral.light} icon="upload" size="15" />
    if (record.ds_moti_crit === 'SEM COTA')
      return <Icon color={theme.colors.alert.warning.dark} icon="report" size="15" />
    if (record.ds_moti_crit === 'SEM ESTOQUE')
      return <Icon color={theme.colors.alert.warning.dark} icon="box" size="15" />
    return ''
  }

  const tableLabels = (): HeadItemType[] => {
    const sizes = [...sizesSet].sort().map((size) => {
      const object: { size: string } = JSON.parse(size)

      return { columnName: object.size, label: object.size }
    })

    return [
      { columnName: '', label: '' },
      { columnName: 'Material', label: 'Material' },
      { columnName: 'DescricaoMaterial', label: 'Descrição' },
      { columnName: 'Cor', label: 'Cor' },
      { columnName: 'DescricaoCor', label: 'Descrição' },
      { columnName: 'Fabrica', label: 'Fábrica' },
      { columnName: 'MotivoReprovacao', label: 'Motivo de reprovação' },
      { columnName: 'Qtd', label: 'Qtd.' },
      { columnName: 'Valor', label: 'Valor (R$)' },
      { columnName: 'Desconto', label: 'Desconto (%)' },
      ...sizes,
    ]
  }

  const tableData = () => {
    const recordsData = rejectedRecords
      .slice(pageSize * (currentPage - 1), pageSize * currentPage)
      .map((record, key) => {
        const sizes = [...sizesSet].sort().map((size) => {
          const object: { size: string } = JSON.parse(size)
          const foundSize = record.tamanhos.find((t) => t.cd_tamh === object.size)
          if (foundSize?.cd_tamh) {
            return record.cd_matr[0] === '7'
              ? numberToDecimalString(foundSize.qt_item)
              : numberToDecimalString(foundSize.qt_item_conv)
          }
          return <S.EmptyValue key={`${key}-${object.size}`}>0</S.EmptyValue>
        })

        return {
          checked: !!record.checked,
          content: [
            record.ds_moti_crit || record.nr_pedi_rasc_dupl ? (
              <Tooltip
                arrow
                key={`icon-${record.cd_matr}-${record.cd_cor}`}
                placement="top"
                title={tooltipLabel(record)}
              >
                <S.IconSpan
                  hasLink={!!record.nr_pedi_rasc_dupl}
                  onClick={() => handleLink(record.nr_pedi_rasc_dupl)}
                >
                  {tooltipIcon(record)}
                </S.IconSpan>
              </Tooltip>
            ) : (
              ''
            ),
            record.cd_matr,
            toAllFirstLetterUpper(record.ds_matr),
            record.cd_cor,
            toAllFirstLetterUpper(record.ds_cor),
            record.cd_cent_matr ?? '--',
            record.ds_moti_repv_anls ?? '-',
            numberToDecimalString(record.qt_tota_item_conv),
            record.vl_tota_item ? formatToPTBRNumber(record.vl_tota_item) : '-',
            record.pc_dsct ? `${record.pc_dsct}%` : <S.EmptyValue>0%</S.EmptyValue>,
            ...sizes,
          ],
          disableCheckbox: !!record.nr_pedi_rasc_dupl,
          id: `${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`,
        }
      })
    return recordsData
  }

  const disableGlobalCheckbox = () => {
    return rejectedRecords.every((record) => record.nr_pedi_rasc_dupl)
  }

  const setMaterialCheck = (checked: boolean, id?: string) => {
    if (id) {
      const [ds_matr, cd_cor, nr_item_pedi_rasc] = id.split('-')

      const newRecords = rejectedRecords.map((record) => {
        if (
          record.cd_matr === ds_matr &&
          record.cd_cor === cd_cor &&
          record.nr_item_pedi_rasc === Number(nr_item_pedi_rasc)
        ) {
          return {
            ...record,
            checked,
          }
        }
        return record
      })
      dispatch(setRejectedRecords(newRecords))
    } else {
      const newRecords = rejectedRecords.map((record) => {
        if (record.nr_pedi_rasc_dupl) return record
        return {
          ...record,
          checked,
        }
      })
      dispatch(setRejectedRecords(newRecords))
    }
  }

  const getCheckedRecords = () => {
    return rejectedRecords.filter((record) => record.checked)
  }

  const navToOrder = (order: string) => {
    navigate(`${URL_CONST.ORDERS}/${order}`)
  }

  const dispatchReplicateRecords = () => {
    dispatch(replicateRecords(getCheckedRecords(), navToOrder))
    setConfirmReplicateModal(false)
  }

  const calcBoxes = () => {
    const valuesToSum = rejectedRecords.map((item) => {
      return item.qt_tota_item
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcPairs = () => {
    const valuesToSum = rejectedRecords.map((item) => {
      return item.qt_tota_item_conv
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcSelectedPairs = () => {
    const valuesToSum = getCheckedRecords().map((item) => {
      return item.qt_tota_item_conv
    })
    return numberToDecimalString(valuesToSum.reduce(sumArray, 0))
  }
  const calcTotal = () => {
    const valuesToSum = rejectedRecords.map((item) => item.vl_tota_item || 0)
    return valuesToSum.reduce(sumArray, 0)
  }

  const toggleFactoryFilterItem = (option: string) => {
    setSelectedFactoryFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const toggleStatusFilterItem = (option: string) => {
    setSelectedStatusFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const appliedFiltersAmount = () => {
    let amount = 0
    if (appliedFactoryFilters.length > 0) amount++
    if (appliedStatusFilters.length > 0) amount++
    return amount
  }

  return (
    <S.Wrapper>
      <S.HeadContainer>
        <S.TitleWrapper>
          <Typography design="hav-h5-text">Materiais não atendidos</Typography>
          <Typography design="hav-helper-text">
            Materiais que não puderam ser solicitados neste pedido, mas que podem ser incluídos em
            um pedido subsequente.
          </Typography>
        </S.TitleWrapper>

        <S.SearchWrapper>
          <div>
            <Input
              addorment
              design={DSInputType.SEARCH_INPUT}
              label="Localizar material"
              onChange={handleSearch}
              placeholder="Ex.: 4000029"
              value={searchValue}
              width={165}
            />
          </div>
          <S.FilterButtonWrapper>
            <S.FilterButton disabled={loading} onClick={() => setOpenFilterModal(true)}>
              <FilterAltOutlinedIcon sx={{ fontSize: '30px' }} />
            </S.FilterButton>
            {appliedFiltersAmount() > 0 && (
              <S.FiltersNumberStyled>{appliedFiltersAmount()}</S.FiltersNumberStyled>
            )}
          </S.FilterButtonWrapper>
        </S.SearchWrapper>
      </S.HeadContainer>

      <S.Divider />
      {loading ? (
        <S.LoadingContainer>
          <LoadingHavaianas />
        </S.LoadingContainer>
      ) : (
        <>
          <S.ActionRow>
            <SlimButton
              content="Reutilizar em novo pedido"
              disabled={!getCheckedRecords().length || shouldDisable(ACTIONS_SCREENS.EDIT)}
              icon={<RedoIcon sx={{ fontSize: '16px' }} />}
              onClick={() => setConfirmReplicateModal(true)}
              variant="secondary"
            />
            <Typography design="hav-default-03-text">
              {getCheckedRecords().length > 0
                ? `${calcSelectedPairs()} pares do saldo selecionados`
                : 'Nenhum par do saldo está selecionado'}
            </Typography>
          </S.ActionRow>

          {rejectedRecords.length ? (
            <S.TableWrapper>
              <Table
                bodyData={tableData()}
                checkboxSize="small"
                disableGlobalCheckbox={disableGlobalCheckbox()}
                hasCheckbox
                headData={tableLabels()}
                iconColumns={[0]}
                rowHeight={32}
                setChecked={setMaterialCheck}
              />
            </S.TableWrapper>
          ) : (
            <S.NotFoundWrapper>
              <NotFound
                description={
                  <S.NotFoundContent>
                    Verifique os critérios utilizados e tente novamente.
                    <SlimButton
                      content="Reiniciar busca e filtros"
                      icon={<ChevronLeft color="info" />}
                      onClick={clearFilters}
                      type="button"
                      variant="white"
                    />
                  </S.NotFoundContent>
                }
                title={'Nenhum resultado encontrado'}
              />
            </S.NotFoundWrapper>
          )}

          <S.SummaryWrapper>
            <div>
              {getTotalItems() > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  itemsPerPage={pageSize}
                  numberOfItems={getTotalItems()}
                  numberOfPages={getTotalPages()}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </div>
            <S.SummaryRow>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • pares</Typography>
                <span>{numberToDecimalString(calcPairs())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • caixas</Typography>
                <span>{numberToDecimalString(calcBoxes())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Valor total</Typography>
                <span>{formatToPTBRNumber(calcTotal())}</span>
              </S.SummaryItem>
            </S.SummaryRow>
          </S.SummaryWrapper>
        </>
      )}

      <Modal
        closeAction={() => {
          setSelectedFactoryFilters(appliedFactoryFilters)
          setSelectedStatusFilters(appliedStatusFilters)
          setOpenFilterModal(false)
        }}
        modalWidth={450}
        open={openFilterModal}
        setOpen={setOpenFilterModal}
        showAction={false}
        title="Filtro de materiais"
        titleSize={22}
      >
        <S.FilterOption>
          <span>Fábrica</span>
          <S.CheckboxRow>
            {factoryFilterOptions.map((option) => (
              <Checkbox
                checked={selectedFactoryFilters.includes(option)}
                key={option}
                label={option}
                onChange={() => toggleFactoryFilterItem(option)}
                size="small"
              />
            ))}
          </S.CheckboxRow>
        </S.FilterOption>

        <S.FilterOption>
          <span>Status</span>
          <S.CheckboxList>
            {Object.keys(materialStatusOptions).map((option) => (
              <Checkbox
                checked={selectedStatusFilters.includes(option)}
                key={option}
                label={
                  <S.IconSpan>
                    {materialStatusOptions[option].icon}
                    {materialStatusOptions[option].label}
                  </S.IconSpan>
                }
                onChange={() => toggleStatusFilterItem(option)}
                size="small"
              />
            ))}
          </S.CheckboxList>
        </S.FilterOption>

        <S.ModalActionsRow>
          <SlimButton content="Limpar filtros" onClick={clearFilters} variant="secondary" />
          <SlimButton
            content="Filtrar"
            onClick={() => {
              setAppliedFactoryFilters(selectedFactoryFilters)
              setAppliedStatusFilters(selectedStatusFilters)
              dispatch(getRejectedItems(searchValue, selectedFactoryFilters, selectedStatusFilters))
              setOpenFilterModal(false)
            }}
            variant="primary"
          />
        </S.ModalActionsRow>
      </Modal>
      <Modal
        closeAction={() => setConfirmReplicateModal(false)}
        description={
          <span>
            Ao continuar será gerado um novo pedido contendo todos os materiais selecionados no
            saldo do pedido atual. <br /> <br />
            Esses materiais ainda estarão disponíveis para consulta neste pedido, mas serão
            sinalizados como encaminhados para um novo pedido.
          </span>
        }
        modalWidth={450}
        open={confirmReplicateModal}
        setOpen={setConfirmReplicateModal}
        showAction={false}
        title="Reutilizar em novo pedido"
        titleSize={22}
      >
        <S.ModalActionsRow>
          <SlimButton
            content="Voltar"
            onClick={() => setConfirmReplicateModal(false)}
            variant="secondary"
          />
          <SlimButton
            content="Criar novo pedido"
            onClick={dispatchReplicateRecords}
            variant="primary"
          />
        </S.ModalActionsRow>
      </Modal>
    </S.Wrapper>
  )
}

export default RejectedItemsTab
